import _ from './fade.module.scss'
import React, {useEffect, useRef, useState} from 'react'
import {$$$, $} from '../shared/class-name'

export const FADE_DURATION = 150

export const FadeGate = ({visible, children, collapse = false, as: Component = 'div', className, visibleOverflow}) => {
    const [present, setPresent] = useState(visible)
    const [shown, setShown] = useState(visible)
    const visibleChildren = useRef(children)
    if (visible) visibleChildren.current = children

    useEffect(() => {
        if (visible) {
            setPresent(true)
            return
        }

        setShown(false)
    }, [visible])

    useEffect(() => {
        if (!present || !visible) return
        if (prefersReducedMotion()) {
            setShown(true)
            return
        }

        setShown(false)
        let secondFrame
        const firstFrame = requestAnimationFrame(() => {
            secondFrame = requestAnimationFrame(() => setShown(true))
        })

        return () => {
            cancelAnimationFrame(firstFrame)
            if (secondFrame) cancelAnimationFrame(secondFrame)
        }
    }, [present, visible])

    useEffect(() => {
        if (visible || !present) return

        const timeout = setTimeout(() => {
            setPresent(false)
            visibleChildren.current = null
        }, FADE_DURATION)

        return () => {
            clearTimeout(timeout)
        }
    }, [present, visible])

    if (!present) return null

    const retainedChildren = visible ? children : visibleChildren.current
    const content = typeof retainedChildren === 'function' ? retainedChildren(shown) : retainedChildren
    if (!collapse) return content
    const horizontal = collapse === 'horizontal'
    const both = collapse === 'both'
    return <Component className={$$$(
        _.collapsible,
        $(_.collapsibleHorizontal, horizontal),
        $(_.collapsibleBoth, both),
        $(_.collapsibleVisible, shown),
        $(className),
    )}>
        <Component className={$$$(_.collapsibleInner, $(_.visibleOverflow, visibleOverflow))}>{content}</Component>
    </Component>
}

export const Fade = ({visible, children, className, style, zIndex, as: Component = 'div', ...props}) => {
    const [present, setPresent] = useState(visible)
    const [shown, setShown] = useState(visible)
    const [transitioning, setTransitioning] = useState(false)
    const hasRootProps = !!className || !!style || Object.keys(props).length > 0

    useEffect(() => {
        if (visible) {
            setPresent(true)
            return
        }

        setShown(false)
    }, [visible])

    useEffect(() => {
        if (!present || !visible) return
        if (prefersReducedMotion()) {
            setShown(true)
            setTransitioning(false)
            return
        }

        setShown(false)
        setTransitioning(true)
        let secondFrame
        const firstFrame = requestAnimationFrame(() => {
            secondFrame = requestAnimationFrame(() => setShown(true))
        })
        let releaseFrame
        const timeout = setTimeout(() => {
            releaseFrame = requestAnimationFrame(() => setTransitioning(false))
        }, FADE_DURATION)

        return () => {
            cancelAnimationFrame(firstFrame)
            if (secondFrame) cancelAnimationFrame(secondFrame)
            if (releaseFrame) cancelAnimationFrame(releaseFrame)
            clearTimeout(timeout)
        }
    }, [present, visible])

    useEffect(() => {
        if (visible || !present) return

        setTransitioning(true)
        const timeout = setTimeout(() => {
            setPresent(false)
            setTransitioning(false)
        }, FADE_DURATION)

        return () => {
            clearTimeout(timeout)
        }
    }, [present, visible])

    if (!present) return null
    if (visible && shown && !transitioning && !hasRootProps) return children

    return <Component
        {...props}
        className={$$$(_.root, $(_.visible, shown), $(className))}
        style={{...style, ...((transitioning || !visible) && zIndex != null ? {zIndex} : {}), '--fade-presence-duration': `${FADE_DURATION}ms`}}
    >
        {children}
    </Component>
}
const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
